import { useEffect, useRef } from 'react'

// Welt: spitze Sechsecke in der Bodenebene (x nach rechts, z in die Tiefe), Hoehe y wogt.
const S = 0.6
const HB = Math.sqrt(3) * S
const SPALTEN = 116
const REIHEN = 62
const KAMERA_HOEHE = 4.6
const Z_VERSATZ = 4.5
// vier Ecken je Wabe: oben, oben rechts, unten rechts, unten - die anderen drei Kanten zeichnen die Nachbarn
const OX = [0, HB / 2, HB / 2, 0]
const OZ = [-S, -S / 2, S / 2, S]
// Lichtpunkt: x, y, Radius, Deckkraft
type Funke = [number, number, number, number]

/** Das Wabennetz der Karte als wogende Flaeche. Die Maus hebt die Waben unter sich an.
 *  Laeuft nur, solange der Aufmacher sichtbar ist; mit reduzierter Bewegung steht ein Standbild. */
export default function Wabenfeld({ className = '' }: Readonly<{ className?: string }>) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return undefined
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let w = 0, h = 0, f = 0, horizont = 0
    let raf = 0, laeuft = false, imBild = false, zuletzt = 0, t = 4
    const maus = { x: 0, z: 0, staerke: 0, ziel: 0 }

    const groesse = () => {
      const r = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = r.width
      h = r.height
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      f = Math.max(w, 720) * 0.5
      horizont = h * 0.16
    }

    const hoehe = (x: number, z: number) => {
      let y = Math.sin(x * 0.3 + t * 0.5) * Math.cos(z * 0.2 - t * 0.32) * 0.8
        + Math.sin((x * 0.5 + z) * 0.11 + t * 0.22) * 1.5
        + x * 0.035 // steigt nach rechts leicht an, wie auf der Karte
      if (maus.staerke > 0.01) {
        const dx = x - maus.x, dz = z - maus.z
        y += maus.staerke * 1.9 * Math.exp(-(dx * dx + dz * dz) / 14)
      }
      return y
    }

    // vier Ecken einer Wabe als Linienzug; auf Wellenkaemmen einen Lichtpunkt merken
    const wabeZeichnen = (cx: number, cz: number, mitFunke: boolean, a: number, funken: Funke[]) => {
      for (let k = 0; k < 4; k++) {
        const x = cx + OX[k], z = cz + OZ[k], d = z + Z_VERSATZ
        const y = hoehe(x, z)
        const px = w / 2 + (x * f) / d
        const py = horizont + ((KAMERA_HOEHE - y) * f) / d
        if (k > 0) {
          ctx.lineTo(px, py)
          continue
        }
        ctx.moveTo(px, py)
        if (mitFunke && y > 2.1 && px > -10 && px < w + 10) funken.push([px, py, Math.min(2.6, 14 / d) + 0.4, a * Math.min(1, (y - 2.1) * 1.6)])
      }
    }

    const reiheZeichnen = (j: number, funken: Funke[]) => {
      const cz = j * 1.5 * S
      const a = Math.max(0, 1 - (cz + Z_VERSATZ) / 62) ** 1.4
      if (a < 0.02) return
      const versatz = (j % 2) * HB / 2
      ctx.beginPath()
      for (let i = 0; i < SPALTEN; i++) wabeZeichnen((i - SPALTEN / 2) * HB + versatz, cz, i % 2 === 0, a, funken)
      ctx.strokeStyle = `rgba(196,244,236,${(a * 0.55).toFixed(3)})`
      ctx.stroke()
    }

    const zeichnen = () => {
      ctx.clearRect(0, 0, w, h)
      ctx.lineWidth = 1
      const funken: Funke[] = []
      for (let j = REIHEN - 1; j >= 0; j--) reiheZeichnen(j, funken)
      // Lichtpunkte auf den Wellenkaemmen
      for (const [x, y, r, al] of funken) {
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(160,255,238,${(al * 0.9).toFixed(3)})`
        ctx.fill()
      }
    }

    const bild = (jetzt: number) => {
      const dt = Math.min(0.05, (jetzt - zuletzt) / 1000)
      zuletzt = jetzt
      t += dt
      maus.staerke += (maus.ziel - maus.staerke) * 0.06
      zeichnen()
      if (laeuft) raf = requestAnimationFrame(bild)
    }
    const start = () => {
      if (laeuft || still || !imBild || document.hidden) return
      laeuft = true
      zuletzt = performance.now()
      raf = requestAnimationFrame(bild)
    }
    const stopp = () => { laeuft = false; cancelAnimationFrame(raf) }

    const bewegen = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const r = canvas.getBoundingClientRect()
      const sx = e.clientX - r.left, sy = e.clientY - r.top
      if (sy <= horizont + 30 || sy > h || sx < 0 || sx > w) { maus.ziel = 0; return }
      const d = (KAMERA_HOEHE * f) / (sy - horizont) // Tiefe auf Hoehe 0 unter dem Mauszeiger
      maus.z = d - Z_VERSATZ
      maus.x = ((sx - w / 2) * d) / f
      maus.ziel = 1
    }
    const sichtbarkeit = () => (document.hidden ? stopp() : start())

    groesse()
    zeichnen()
    const ro = new ResizeObserver(() => { groesse(); if (!laeuft) zeichnen() })
    ro.observe(canvas)
    const io = new IntersectionObserver(([e]) => { imBild = e.isIntersecting; if (imBild) start(); else stopp() })
    io.observe(canvas)
    window.addEventListener('pointermove', bewegen, { passive: true })
    document.addEventListener('visibilitychange', sichtbarkeit)
    return () => {
      stopp()
      ro.disconnect()
      io.disconnect()
      window.removeEventListener('pointermove', bewegen)
      document.removeEventListener('visibilitychange', sichtbarkeit)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 bottom-0 -z-10 w-full ${className}`}
      style={{ maskImage: 'linear-gradient(to bottom, transparent 0%, transparent 18%, #000 52%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, transparent 18%, #000 52%)' }}
    />
  )
}
